import { supabase } from '@/lib/supabase';
import type { Article } from '@/types';
import { logQuantityAdjusted } from '@/utils/activityLogger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Save } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface LocationState {
  article?: Article;
}

export default function QuickAdjust() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const state = location.state as LocationState;
  const article = state?.article;

  if (!article) {
    navigate('/articles');
    return null;
  }

  const [adjustment, setAdjustment] = useState<number>(0);
  const [note, setNote] = useState<string>('');

  const newQuantity = (article.quantita_magazzino_attuale || 0) + adjustment;

  // Update quantity mutation
  const updateQuantity = useMutation<any, Error, { newQty: number; note: string }>({
    mutationFn: async ({ newQty, note }) => {
      const { error } = await supabase
        .from('articles')
        .update({
          quantita_magazzino_attuale: newQty,
          note: note || article.note
        })
        .eq('id', article.id);

      if (error) throw error;
      return { newQty, note };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['articles'] });
      logQuantityAdjusted(article.id, article.codice_prodotto, adjustment, data.newQty);
      navigate('/articles');
    },
    onError: (error) => {
      alert(`Errore: ${error.message}`);
    }
  });

  const handleSave = () => {
    if (adjustment === 0) {
      alert('Nessuna modifica da salvare');
      return;
    }
    if (newQuantity < 0) {
      alert('La quantità non può essere negativa');
      return;
    }
    updateQuantity.mutate({ newQty: newQuantity, note });
  };

  const quickAdjust = (amount: number) => {
    setAdjustment(prev => prev + amount);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="p-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/articles')}
            className="p-2 hover:bg-slate-100 rounded transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Regola Quantità</h1>
          <div className="w-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 max-w-2xl mx-auto">
        {/* Article Info */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{article.codice_prodotto}</h2>
          {article.descrizione_prodotto && (
            <p className="text-slate-600 mb-4">{article.descrizione_prodotto}</p>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-600">Quantità Attuale</p>
              <p className="text-2xl font-bold text-slate-800">{article.quantita_magazzino_attuale || 0}</p>
            </div>
            <div>
              <p className="text-slate-600">Quantità Minima</p>
              <p className="text-lg font-semibold text-slate-600">{article.quantita_minima_magazzino || 0}</p>
            </div>
          </div>
        </div>

        {/* Adjustment Controls */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Regolazione</h3>

          {/* Quick adjust buttons */}
          <div className="grid grid-cols-5 gap-2 mb-6">
            <button
              onClick={() => quickAdjust(-10)}
              className="bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-3 rounded-lg transition-colors touch-manipulation"
            >
              -10
            </button>
            <button
              onClick={() => quickAdjust(-5)}
              className="bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-3 rounded-lg transition-colors touch-manipulation"
            >
              -5
            </button>
            <button
              onClick={() => quickAdjust(-1)}
              className="bg-red-100 hover:bg-red-200 text-red-700 font-semibold py-3 rounded-lg transition-colors touch-manipulation"
            >
              -1
            </button>
            <button
              onClick={() => quickAdjust(1)}
              className="bg-green-100 hover:bg-green-200 text-green-700 font-semibold py-3 rounded-lg transition-colors touch-manipulation"
            >
              +1
            </button>
            <button
              onClick={() => quickAdjust(5)}
              className="bg-green-100 hover:bg-green-200 text-green-700 font-semibold py-3 rounded-lg transition-colors touch-manipulation"
            >
              +5
            </button>
          </div>

          {/* Manual input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Regolazione Manuale
            </label>
            <input
              type="number"
              value={adjustment}
              onChange={(e) => setAdjustment(parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-xl text-center font-bold"
            />
          </div>

          {/* Result preview */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-medium">Nuova Quantità</p>
                <p className="text-3xl font-bold text-blue-900">{newQuantity}</p>
              </div>
              {adjustment !== 0 && (
                <div className={`text-2xl font-bold ${adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {adjustment > 0 ? '+' : ''}{adjustment}
                </div>
              )}
            </div>
            {newQuantity < 0 && (
              <p className="text-sm text-red-600 font-medium mt-2">⚠️ La quantità non può essere negativa</p>
            )}
          </div>
        </div>

        {/* Note */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Note (opzionale)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Motivo della regolazione..."
            rows={3}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-base"
          />
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
        <div className="max-w-2xl mx-auto flex gap-3">
          <button
            onClick={() => navigate('/articles')}
            className="flex-1 px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg transition-colors touch-manipulation font-semibold"
          >
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={updateQuantity.isPending || adjustment === 0 || newQuantity < 0}
            className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg transition-colors touch-manipulation font-semibold flex items-center justify-center gap-2"
          >
            <Save className="w-5 h-5" />
            {updateQuantity.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}